import React, { useEffect } from "react";
import { Badge, Button } from "reactstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import YearPicker from "components/customs/daterange";
import { hasPermission } from "utility/helpers";
import {
  ACCOUNT_EDITOR,
  COMMITTEE_VIEW,
  DIRECTORY_VIEW,
  HIDDEN_AFFILIATIONS_VIEW,
  HIDDEN_PROGRAMS_VIEW,
  HIDDEN_REPRESENTATIVES_VIEW,
  canEditRoles,
} from "utility/constants";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import useAuth from "hooks/useAuth";
import { fetchPendingProjects } from "../../../redux/actions/projects";

import ImgContainer from "../../common/img";
import UserDropdown from "../dropdown/user";
import horTxtImage from "../../../assets/img/20thCong.png";

const StyledNav = styled.nav`
  background: ${({ isHomePage }) => (isHomePage ? "transparent" : "white")};
  border-radius: 25px;
  box-shadow: ${({ isHomePage }) =>
    isHomePage ? "none" : "4px 9px 17px -1px #00000014"};
  padding: 18px;

  @media (min-width: 992px) {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 10px;
  }

  @media (min-width: 1400px) {
    grid-template-columns: ${({ isHomePage }) =>
      isHomePage ? "1fr auto" : "1fr 2fr auto"};
  }
`;

const NavBrand = styled.a`
  display: ${({ isHomePage }) => (isHomePage ? "none !important" : "block")};
`;

const RightNavSection = styled.div`
  display: grid;
  grid-template-columns: auto auto auto;
  justify-content: end;
  gap: 10px;

  @media (max-width: 575.98px) {
    grid-template-columns: 2fr auto;
  }
`;

const SendRequestButton = styled(Button)`
  background: ${({ theme }) => theme.colors.buttonBlue};
  border-radius: 14px;
  font-weight: 500;
  font-size: 15px;
  max-width: 144px;
  height: 36px;
  display: flex;
  justify-content: center;
  border: none;
`;

const SendRequestButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NavItemsContainer = styled.ul`
  display: grid;
  gap: 10px;
  margin: 0;
  padding: 0;
  grid-template-columns: auto auto auto auto auto auto auto;
  align-items: center;
`;

const StyledList = styled.li`
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  height: 36px;

  a {
    text-decoration: none;
    font-weight: 500;
    font-size: 15px;
    color: black;

    &.white-text {
      color: white;
    }
  }

  :has(.active) {
    background: ${({ theme }) => theme.colors.red};
    border-radius: 14px;

    a {
      color: white;
    }
  }

  .badge {
    position: absolute;
    top: -8px;
    right: -8px;
    z-index: 1;
    background-color: ${({ theme }) => theme.colors.secondaryBlue} !important;
  }
`;

const NavbarUser = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const role = user?.type;

  const dispatch = useDispatch();

  const { pendingProjects } = useSelector((state) => state.projects);

  const isHomePage = location.pathname === "/home" || location.pathname === "/";

  const isVisited = (url) => {
    const path = location.pathname;

    if (url === "/home") {
      // Match "/home" or root "/"
      return isHomePage ? "active" : "";
    }

    return path.includes(url) ? "active" : "";
  };

  window.onload = () => {
    isVisited();
  };

  const navItems = [
    {
      label: "Home",
      link: "/home",
      showItem: hasPermission("landing"),
      className: `${isVisited("/home")}
      ${isHomePage ? "white-text" : ""}`,
    },
    {
      label: "Representatives",
      link: "/representatives",
      showItem:
        !HIDDEN_REPRESENTATIVES_VIEW.includes(role) &&
        hasPermission("congressman-view"),
      className: `${isVisited("/representatives")}
      ${isHomePage ? "white-text" : ""}`,
    },
    {
      label: "Programs",
      link: "/projects",
      showItem:
        !HIDDEN_PROGRAMS_VIEW.includes(role) && hasPermission("project-view"),
      className: `${isVisited("/projects")}
      ${isHomePage ? "white-text" : ""}`,
    },
    {
      label: "Affiliations",
      link: "/parties",
      showItem:
        !HIDDEN_AFFILIATIONS_VIEW.includes(role) && hasPermission("party-view"),
      className: `${isVisited("/parties")}
      ${isHomePage ? "white-text" : ""}`,
    },
    {
      label: "Directory",
      link: "/directory",
      showItem: DIRECTORY_VIEW.includes(role),
      className: `${isVisited("/directory")}
      ${isHomePage ? "white-text" : ""}`,
    },
    {
      label: "Events",
      link: "/events",
      showItem: canEditRoles.includes(role),
      className: `${isVisited("/events")}
      ${isHomePage ? "white-text" : ""}`,
    },
    {
      label: "Committee",
      link: "/committee",
      showItem: COMMITTEE_VIEW.includes(role),
      className: `${isVisited("/committee")}
      ${isHomePage ? "white-text" : ""}`,
    },
  ];

  useEffect(() => {
    if (role === ACCOUNT_EDITOR && !pendingProjects) {
      dispatch(fetchPendingProjects());
    }
  }, [pendingProjects]);

  return (
    <StyledNav
      className="sticky-top"
      isHomePage={isHomePage}
    >
      <NavBrand
        isHomePage={isHomePage}
        className="d-none d-xxl-flex"
        href="/"
        onClick={(e) => {
          e.preventDefault();

          if (hasPermission("landing")) {
            navigate("/");
          }
        }}
      >
        <ImgContainer
          src={horTxtImage}
          height="84px"
          width="290px"
        />
      </NavBrand>

      <NavItemsContainer className="d-none d-lg-grid">
        {navItems.map(
          ({ showItem, link, label, className }) =>
            showItem && (
              <StyledList key={`${link}${label}`}>
                <Link
                  to={link}
                  className={className}
                >
                  {label}
                </Link>
                {label === "Programs" && role === ACCOUNT_EDITOR && (
                  <Badge
                    className={`${
                      pendingProjects === 0 || pendingProjects === null
                        ? "d-none"
                        : ""
                    }`}
                    pill
                  >
                    {pendingProjects}
                  </Badge>
                )}
              </StyledList>
            )
        )}
      </NavItemsContainer>

      <RightNavSection>
        {hasPermission("project-create") && (
          <SendRequestButtonContainer className="d-sm-flex d-none d-lg-flex">
            <SendRequestButton onClick={() => navigate("/add-project")}>
              {"Send Request"}
            </SendRequestButton>
          </SendRequestButtonContainer>
        )}

        <YearPicker />
        <UserDropdown />
      </RightNavSection>
    </StyledNav>
  );
};

export default NavbarUser;
